import React, { useEffect } from 'react'
import CustomSelect from 'designer/component/element/Select'
import objectPath from 'object-path'
import { copyDataByCondition } from 'designer/component/action/Condition'
import { isEmpty } from 'designer/component/action/condition/State'
import { onUpdateCondition } from 'designer/component/action/condition/Action'
import { getStorage } from 'designer/component/action/Storage'
import {
  updateFieldValue,
  updateMapResponse,
  updateWidgetData,
} from './DataControl'
import PropWrapper from './PropWrapper'

const Children = ({ dataProps }) => {
  const { props, designContext, fieldProps, fieldName, fieldKey, errors } =
    dataProps
  const {
    jsonForm,
    setFormData,
    isResetData,
    elementAction,
    setFieldFormData,
    getCurrentUi,
    isUiChanged,
    setIsUiChanged,
  } = designContext
  const { uischema, config } = props
  let { label, optionType, valueJoin = '', valueType, isMulti } = uischema
  const formData = jsonForm.formData || {}
  const {
    uiKey,
    uiName,
    value,
    data,
    defaultValue,
    renderWidget,
    mapResponse,
  } = fieldProps

  const optionValue =
    objectPath.get(uischema, `${optionType}.optionValue`) || 'value'
  const valueKey = objectPath.get(uischema, `${optionType}.valueKey`)

  const addRuleUiKey = (keys = [], key) => {
    if (!key) return keys
    if (keys.includes(key)) return keys

    keys.push(key)
    return keys
  }

  const addRuleFieldKeys = (keys = [], field) => {
    addRuleUiKey(keys, field)
    if (!field) return keys

    let ui = getCurrentUi(field)
    if (ui.key === field) {
      addRuleUiKey(keys, ui.name)
    }
    return keys
  }

  const getSelectRuleUiKeys = () => {
    let keys = []
    const otherValues = objectPath.get(uischema, `${optionType}.otherValues`)

    if (otherValues) {
      Object.keys(otherValues).forEach((key) => {
        let { field } = otherValues[key]
        addRuleFieldKeys(keys, field)
      })
    }
    if (valueKey) {
      Object.keys(valueKey).forEach((key) => {
        let name = objectPath.get(valueKey, key)
        addRuleFieldKeys(keys, name)
      })
    }
    return keys
  }

  const updateValue = (value, isOnChange, config = {}) => {
    updateFieldValue({ ...dataProps, value, isOnChange, ...config })
  }

  const setValue = (value) => {
    if (isMulti && value) {
      let preview = []
      value.forEach((item) => {
        let itemValue = objectPath.get(item, optionValue)
        if (itemValue) {
          preview.push(itemValue)
        }
      })
      if (valueType === 'string') {
        return preview.join(valueJoin)
      }
      return preview
    }
    return objectPath.get(value, optionValue)
  }

  const createOptionValue = (value) => {
    if (value === undefined || value === null) return value
    if (typeof value === 'object') return value
    let option = {}
    objectPath.set(option, optionValue, value)
    return option
  }

  const createDefaultOption = (value) => {
    if (isMulti && value) {
      if (valueType === 'string' && !Array.isArray(value)) {
        value = value.split(valueJoin)
      }
      if (Array.isArray(value)) {
        return value.map((item) => createOptionValue(item))
      }
    }
    return createOptionValue(value)
  }

  const isEmptySelectValue = (value) => {
    if (value === '' || value === undefined || value === null) return true
    if (Array.isArray(value) && value.length === 0) return true
    if (typeof value === 'object' && Object.keys(value).length === 0) return true

    return false
  }

  const setOtherFieldFormData = (name, value) => {
    let formData = jsonForm.formData || {}
    objectPath.set(formData, name, value)
    objectPath.set(jsonForm, 'formData', formData)
    setFieldFormData(name, value)
  }

  const updateOtherValue = (option = {}) => {
    const otherValues = objectPath.get(uischema, `${optionType}.otherValues`)
    if (otherValues) {
      Object.keys(otherValues).forEach((key) => {
        let value = objectPath.get(option, key)
        let { field } = otherValues[key]
        let ui = getCurrentUi(field)
        if (ui.key === field) {
          setOtherFieldFormData(ui.name, value)
        }
        objectPath.set(isUiChanged, field, { isRender: true })
        setOtherFieldFormData(field, value)
      })
    } else if (valueKey) {
      Object.keys(valueKey).forEach((key) => {
        let value = objectPath.get(option, key)
        let name = objectPath.get(valueKey, key)
        setOtherFieldFormData(name, value)
      })
    }
    setIsUiChanged(isUiChanged)
  }

  const updateData = (option, isOnChange) => {
    let newValue = setValue(option)
    updateOtherValue(option)
    updateValue(newValue, isOnChange, {
      ruleUiKeys: getSelectRuleUiKeys(),
    })
  }

  const copyData = (option) => {
    let newValue = setValue(option)
    let newFormData = copyDataByCondition(
      newValue,
      elementAction,
      uiKey,
      formData,
    )
    setFormData(newFormData)
  }

  useEffect(() => {
    let hasMapResponse =
      objectPath.get(formData, ['@mapResponse', uiKey]) ||
      objectPath.get(formData, ['@mapResponse', uiName])
    let hasDefaultValue = !isEmptySelectValue(defaultValue)
    let isEmptyValue = !hasMapResponse && value === '' && hasDefaultValue
    if (value === undefined || value === null || isEmptyValue || isResetData) {
      let storage = getStorage(jsonForm, uiName)
      let option = {}
      if (isEmpty(storage)) {
        option = createDefaultOption(defaultValue)
      } else {
        objectPath.set(option, optionValue, storage)
      }
      updateData(option, true)
    } else if (value === defaultValue) {
      let option = createDefaultOption(defaultValue)
      updateData(option, true)
    }
  }, [defaultValue, isResetData])

  useEffect(() => {
    updateMapResponse(dataProps)
  }, [mapResponse])

  useEffect(() => {
    updateWidgetData(dataProps)
  }, [renderWidget])

  return (
    <CustomSelect
      fieldName={fieldName}
      fieldKey={fieldKey}
      config={config}
      label={label}
      uischema={uischema}
      value={value}
      updateData={updateData}
      updateOtherValue={updateOtherValue}
      copyData={copyData}
      errors={errors}
      uiKey={uiKey}
      data={data}
    />
  )
}

const SelectControl = (props) => {
  return (
    <PropWrapper {...props}>
      {({ dataProps }) => <Children dataProps={dataProps} />}
    </PropWrapper>
  )
}

export default SelectControl
